import { useCallback, useEffect, useRef, useState, createContext, useContext, JSX } from 'react';
import { useUserContext } from '../user_context/UserProvider';
import { isAdmin } from '../../components/routing/UserRoutes/AcceptedRoles';
import { useUserUpdaterProvider } from '../user_context/UserUpdaterProvider';
import { getAllOrganizationGroupMembers } from '../../repository/OrganizationDAO';
import { OrgUser } from '../../models/User/OrgUser';
import Group from '@/models/Organization/Group';
import {
  CompassUnifiedConfigurationOnAzure,
  instanceOfMiniOrganization,
  instanceOfOrganizationOnAzure,
  MiniOrganization,
  OrgJoinRequestItem,
  OrganizationOnAzure,
} from '../../../generated-client/models';
import { getAllJoinRequests } from '@/repository/JoinRequestsDAO';
import { getAllPvdConfigurations } from '@/repository/PvdConfigDAO';
import { getAllEmergencyConfigurations } from '@/repository/EmergencyConfigDAO';
import type { PvdConfigurationOnAzure, EmergencyConfigurationOnAzure } from '@/../generated-client/models';

type JoinRequestsStatus = 'idle' | 'loading' | 'success' | 'error';

type JoinRequestsState = {
  status: JoinRequestsStatus;
  data: OrgJoinRequestItem[];
};

// `groups`-loading reuses this exact status vocabulary (idle/loading/success/error) rather
// than inventing a second, different status-representation pattern in the same provider - see
// `loadGroups` below for why an explicit status was introduced for groups at all.
type GroupsStatus = JoinRequestsStatus;

type GroupsState = {
  status: GroupsStatus;
  data: Group[];
};

type OrganizationContextType = {
  groups: Array<Group>;
  // Explicit load-status signal for `groups`, mirroring `joinRequestsStatus`/
  // `joinRequestsError` below. Lets a total load failure (every group's fetch rejecting) be
  // distinguished from a genuine success instead of silently keeping stale data (see
  // `loadGroups`'s comments).
  groupsStatus: GroupsStatus;
  groupsError: boolean;
  getUsersGroup: (
    organization: OrganizationOnAzure | MiniOrganization,
    authId: string,
  ) => string;
  organization: OrganizationOnAzure | null;
  updateGroupLocationSetting: (
    groupName: string,
    allowLocationTracking: boolean,
  ) => void;
  updateOrganization: (
    newOrgData: OrganizationOnAzure | MiniOrganization,
  ) => void;
  setUser: (updatedUser: OrgUser) => void;
  joinRequests: OrgJoinRequestItem[];
  joinRequestsStatus: JoinRequestsStatus;
  joinRequestsError: boolean;
  setJoinRequests: (joinRequests: OrgJoinRequestItem[]) => void;
  pvdConfigurations: Array<PvdConfigurationOnAzure> | undefined;
  emergencyConfigurations: Array<EmergencyConfigurationOnAzure> | undefined;
  loadPvdConfigs: () => Promise<void>;
  loadEmergencyConfigs: () => Promise<void>;
  loadAllConfigs: () => Promise<void>;
  loadGroups: () => Promise<void>;
  pvdConfigurationError: boolean;
  emergencyConfigurationError: boolean;
  // Kept for the cleanup phase — both still expose the legacy slot pointing
  // at pvdConfigurations so the dashboard consumers can be moved in Phase H
  // without breaking compilation in this step. Remove in Task 30.
  configurations: Array<CompassUnifiedConfigurationOnAzure> | undefined;
  loadConfigs: () => Promise<void>;
  configurationError: boolean;
};

export const OrganizationContext = createContext<OrganizationContextType>({
  groups: [],
  groupsStatus: 'idle',
  groupsError: false,
  organization: null,
  pvdConfigurations: [],
  emergencyConfigurations: [],
  pvdConfigurationError: false,
  emergencyConfigurationError: false,
  loadPvdConfigs: async () => {},
  loadEmergencyConfigs: async () => {},
  loadAllConfigs: async () => {},
  loadGroups: async () => {},
  configurations: [],
  configurationError: false,
  getUsersGroup: (
    _organization: OrganizationOnAzure | MiniOrganization,
    _authId: string,
  ) => '',
  updateOrganization: (_newOrgData: OrganizationOnAzure | MiniOrganization) => {
    // no-op default
  },
  setUser: (_updatedUser: OrgUser) => {
    // no-op default
  },
  joinRequests: [],
  joinRequestsStatus: 'idle',
  joinRequestsError: false,
  setJoinRequests: (_joinRequests: OrgJoinRequestItem[]) => {
    // no-op default
  },
  loadConfigs: async () => {},
  updateGroupLocationSetting: (_: string, __: boolean) => {},
});
export const OrganizationProvider = ({
  children,
}: {
  children?: JSX.Element;
}) => {
  const { orgUser } = useUserUpdaterProvider();
  const [organization, setOrganization] = useState<OrganizationOnAzure | null>(
    null,
  );
  const { bearerToken, refreshEntitlements } = useUserContext();

  const [pvdConfigurations, setPvdConfigurations] = useState<PvdConfigurationOnAzure[]>();
  const [emergencyConfigurations, setEmergencyConfigurations] = useState<EmergencyConfigurationOnAzure[]>();
  const [pvdConfigurationError, setPvdConfigurationError] = useState(false);
  const [emergencyConfigurationError, setEmergencyConfigurationError] = useState(false);
  const [groupsState, setGroupsState] = useState<GroupsState>({
    status: 'idle',
    data: [],
  });
  const groups = groupsState.data;
  const groupsStatus = groupsState.status;
  const groupsError = groupsStatus === 'error';
  // `setUser`/`updateGroupLocationSetting` further below only ever need to transform the
  // current group list in place (they don't affect load status), so this thin wrapper keeps
  // their existing `setGroups((prevGroups) => ...)` call sites unchanged.
  const setGroups = (updater: (prevGroups: Group[]) => Group[]) => {
    setGroupsState((prevState) => ({
      ...prevState,
      data: updater(prevState.data),
    }));
  };
  // Monotonic id guarding `loadGroups` below against overlapping in-flight calls - see that
  // function's header comment for the full rationale (root cause #1).
  const loadGroupsRequestIdRef = useRef(0);
  const [joinRequestsState, setJoinRequestsState] = useState<JoinRequestsState>(
    {
      status: 'idle',
      data: [],
    },
  );

  const setJoinRequests = (updatedJoinRequests: OrgJoinRequestItem[]) => {
    setJoinRequestsState({
      status: 'success',
      data: updatedJoinRequests,
    });
  };
  const joinRequests = joinRequestsState.data;
  const joinRequestsStatus = joinRequestsState.status;
  const joinRequestsError = joinRequestsStatus === 'error';

  const loadPvdConfigs = async () => {
    if (!orgUser) return;
    try {
      const configs = await getAllPvdConfigurations(orgUser.org_code);
      setPvdConfigurations(configs);
    } catch (error) {
      console.error('Error fetching PVD configurations:', error);
      setPvdConfigurationError(true);
    }
  };

  const loadEmergencyConfigs = async () => {
    if (!orgUser) return;
    try {
      const configs = await getAllEmergencyConfigurations(orgUser.org_code);
      setEmergencyConfigurations(configs);
    } catch (error) {
      console.error('Error fetching Emergency configurations:', error);
      setEmergencyConfigurationError(true);
    }
  };

  const loadAllConfigs = async () => {
    await Promise.all([loadPvdConfigs(), loadEmergencyConfigs()]);
  };

  useEffect(() => {
    if (orgUser && isAdmin(orgUser.role)) {
      loadAllConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgUser, bearerToken]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!orgUser) {
        if (!cancelled) {
          setJoinRequestsState({
            status: 'idle',
            data: [],
          });
        }
        return;
      }

      if (!isAdmin(orgUser.role)) {
        if (!cancelled) {
          setJoinRequestsState({
            status: 'idle',
            data: [],
          });
        }
        return;
      }

      if (!cancelled) {
        setJoinRequestsState((prevState) => ({
          status: 'loading',
          data: prevState.data,
        }));
      }

      try {
        const requests = await getAllJoinRequests();
        if (!cancelled) {
          setJoinRequestsState({
            status: 'success',
            data: requests,
          });
        }
      } catch (error) {
        console.error('Error fetching join requests:', error);
        if (!cancelled) {
          setJoinRequestsState({
            status: 'error',
            data: [],
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgUser]);

  // Extracted (not just effect-local) so consumers can force a re-fetch of
  // groups imperatively — e.g. after an action that changes org membership
  // without the `organization` object reference itself changing (see
  // DeviceManagement's Refresh action, which only refetches devices by
  // default and would otherwise leave `groups` stale).
  //
  // Because this function is invoked BOTH by the effect below (on every `organization`/
  // `bearerToken` change) AND imperatively by consumers as described above, overlapping
  // in-flight calls are possible independent of any single effect's lifecycle — a plain
  // effect-cleanup `cancelled` flag (the pattern `FirmwareDetailsDialog.tsx` uses) would only
  // guard runs started by one particular effect invocation, not calls made imperatively
  // outside it. `loadGroupsRequestIdRef` (declared above) is a monotonic id bumped at the
  // start of every call and re-checked immediately before committing state, so a slower,
  // EARLIER call's eventual resolution is a genuine no-op once a newer call has started. This
  // is the actual fix for the measured race: for org `SAFEGDEV`, an earlier/slower run's
  // stale 13-group/40-member result was landing in state AFTER a newer, complete 14-group run
  // had already succeeded, because nothing previously guarded against exactly this.
  const loadGroups = useCallback(async () => {
    if (!organization || !organization.groups) return;

    const requestId = ++loadGroupsRequestIdRef.current;

    setGroupsState((prevState) => ({ status: 'loading', data: prevState.data }));

    const groupNames = Object.keys(organization.groups);

    try {
      const groupPromises = Object.entries(organization.groups).map(
        async ([groupName, groupValue]) => {
          const allUsersData = await getAllOrganizationGroupMembers(
            organization.org_code,
            organization.id,
            groupName,
          );
          const users = allUsersData.map((contact) => {
            return new OrgUser({
              already_accepted_spec_etag: contact.already_accepted_spec_etag,
              auth_id: contact.auth_id,
              first_name: contact.first_name,
              last_name: contact.last_name,
              org_user_azure_id: contact.id,
              phone_number: contact.phone_number,
              role: contact.role,
              is_prototyper: contact.is_prototyper ?? false,
              last_interaction_timestamp: contact.last_interaction_timestamp,
              location_preference: contact.location_preference,
              language_code: contact.language_code,
              object_type: contact.object_type,
              org_code: contact.org_code,
              unique_key: contact.unique_key,
              device_location_permission: contact?.device_location_permission,
              id: contact.id,
              unified_config_id: contact.unified_config_id || '',
              pvd_config_id: contact.pvd_config_id ?? null,
              emergency_config_id: contact.emergency_config_id ?? null,
              already_accepted_pvd_etag:
                contact.already_accepted_pvd_etag ?? null,
              already_accepted_emergency_etag:
                contact.already_accepted_emergency_etag ?? null,
              should_force_apply_config:
                contact.should_force_apply_config ?? false,
              should_force_apply_pvd_config:
                contact.should_force_apply_pvd_config ?? false,
              should_force_apply_emergency_config:
                contact.should_force_apply_emergency_config ?? false,
            });
          });
          return new Group({
            allow_group_location_tracking:
              groupValue.allow_group_location_tracking,
            group_name: groupName,
            group_id: groupValue.group_id,
            group_value: users,
            connect_user_auth_ids: groupValue.connect_user_auth_ids,
            ops_user_auth_ids: groupValue.ops_user_auth_ids,
            respond_user_auth_ids: groupValue.respond_user_auth_ids,
          });
        },
      );

      // Promise.allSettled, not Promise.all: one group's fetch failing (e.g. because that
      // group was deleted between listing `organization.groups` and fetching its members)
      // must not discard every OTHER group that fetched successfully.
      const settlements = await Promise.allSettled(groupPromises);

      // Bail out here (before touching state) if a newer `loadGroups` call has started since
      // this one began — this run's result is stale even though it finished "successfully";
      // only the most recently-started call is allowed to commit state.
      if (loadGroupsRequestIdRef.current !== requestId) {
        return;
      }

      const failedGroupNames: string[] = [];
      const fetchedGroups = settlements
        .map((settlement, index) => {
          if (settlement.status === 'fulfilled') {
            return settlement.value;
          }
          // Logged by NAME only — never member/user data. A single rejection here is usually
          // because that group was deleted between listing `organization.groups` and
          // fetching its members; that's fine, it's just this one group's row missing this
          // refresh, not a reason to fail the whole batch.
          failedGroupNames.push(groupNames[index]);
          return null;
        })
        // This now does REAL filtering: under the old `Promise.all`, the mapper above always
        // returned a real `Group` and this call could never remove anything (dead code).
        // `Promise.allSettled` can genuinely produce `null`s above for rejected fetches, so
        // this filter is what drops them from the final list.
        .filter((group): group is Group => group !== null);

      if (failedGroupNames.length > 0) {
        console.error(
          'Error fetching groups: the following group(s) failed to load:',
          failedGroupNames,
        );
      }

      if (fetchedGroups.length === 0 && failedGroupNames.length > 0) {
        // Every group's fetch failed — a real error state, not something to silently paper
        // over by leaving the previous `groups` value in place (which would be
        // indistinguishable from a genuine success to every consumer of this context).
        // Mirrors `joinRequestsState`'s existing error representation above — the existing
        // status-representation precedent in this provider — instead of inventing a second,
        // different way to represent load status.
        setGroupsState({ status: 'error', data: [] });
        return;
      }

      const sortedGroups = fetchedGroups.sort((a, b) => {
        if (a.group_name === 'Default') return -1;
        if (b.group_name === 'Default') return 1;
        return a.group_name.localeCompare(b.group_name);
      });

      setGroupsState({ status: 'success', data: sortedGroups });
    } catch (error) {
      // Guards against an unexpected synchronous error (e.g. malformed `organization.groups`)
      // rather than a routine per-group fetch failure — those are now handled above via
      // `Promise.allSettled` and never reach this catch. Still request-id-guarded so a stale
      // run can't clobber a newer run's already-committed state on its way out.
      if (loadGroupsRequestIdRef.current !== requestId) {
        return;
      }
      console.error('Error fetching groups:', error);
      console.log(
        'Error fetching groups, this is likely because you just deleted a group',
      );
    }
  }, [organization]);

  useEffect(() => {
    loadGroups();
    // bearerToken is intentionally included: re-fetch groups on token
    // refresh/re-login even when the `organization` reference is unchanged.
  }, [loadGroups, bearerToken]);

  const getUsersGroup = (
    organization: OrganizationOnAzure | MiniOrganization,
    authId: string,
  ) => {
    if (instanceOfMiniOrganization(organization)) {
      return organization.group_name;
    } else if (instanceOfOrganizationOnAzure(organization)) {
      for (const [groupName, groupData] of Object.entries(
        organization.groups,
      )) {
        if (!groupData) {
          continue;
        }
        if (
          groupData.connect_user_auth_ids?.includes(authId) ||
          groupData.ops_user_auth_ids?.includes(authId) ||
          groupData.respond_user_auth_ids?.includes(authId)
        ) {
          return groupName;
        }
      }
    }

    return '';
  };

  const updateOrganization = (
    newOrgData: OrganizationOnAzure | MiniOrganization,
  ) => {
    refreshEntitlements(newOrgData);
    setOrganization(
      instanceOfMiniOrganization(newOrgData)
        ? miniToRegularOrg(newOrgData)
        : newOrgData,
    );
  };

  /**
   * When the user is a mobile user, instead of getting a full org object, they
   * get a MiniOrganization. However, we are storing a full org, so
   * we have to convert it the best we can
   * @param newOrgData The organization we want to convert to a full Org object
   * @returns
   */
  const miniToRegularOrg = (
    newOrgData: MiniOrganization,
  ): OrganizationOnAzure => {
    return {
      _etag: newOrgData._etag,
      address: '',
      admin_emergency_emails: [],
      company_name: newOrgData.company_name,
      features: [],
      // MiniOrganization only carries the single group the mobile user
      // belongs to (flattened onto the root object), not an array of groups —
      // map that one group through so it isn't silently dropped. Keyed by
      // group_name (not group_id) to match how organization.groups is keyed
      // everywhere else (getUsersGroup, group-member fetches, group display).
      groups: {
        [newOrgData.group_name]: {
          group_id: newOrgData.group_id,
          connect_user_auth_ids: newOrgData.connect_user_auth_ids,
          ops_user_auth_ids: newOrgData.ops_user_auth_ids,
          respond_user_auth_ids: newOrgData.respond_user_auth_ids,
          allow_group_location_tracking:
            newOrgData.allow_group_location_tracking,
        },
      },
      id: newOrgData.id,
      join_org_pin: '',
      object_type: 'organization',
      org_code: newOrgData.org_code,
      phone_number: '',
      primary_contact: '',
      roles_with_features_custom: newOrgData.roles_with_features,
      roles_with_features_default: newOrgData.roles_with_features,
      unique_key: 'organization',
      _ts: newOrgData._ts,
      is_safeguard_organization: false,
      require_join_approval: false,
      // subscription_count is the total purchased pool capacity (seats the
      // org pays for), so it's the sum of the two tier pools.
      subscription_count:
        newOrgData.ops_subscription_count +
        newOrgData.respond_subscription_count,
      ops_subscription_count: newOrgData.ops_subscription_count,
      respond_subscription_count: newOrgData.respond_subscription_count,
      subscription_end_timestamp: newOrgData.subscription_end_timestamp,
      subscription_start_timestamp: newOrgData.subscription_start_timestamp,
      trial_end_timestamp: newOrgData.trial_end_timestamp,
    };
  };
  /**
   *
   * @param updatedUser
   */
  const setUser = (updatedUser: OrgUser) => {
    setGroups((prevGroups) =>
      (prevGroups || []).map((group) => {
        // skip undefined entries
        if (!group) return group;

        // use a safe default for group_value so findIndex won't throw
        const userIndex = (group.group_value || []).findIndex(
          (user) => user.auth_id === updatedUser.auth_id,
        );

        if (userIndex >= 0) {
          // Create a new group_value array with the updated user
          const newGroupValue = [...(group.group_value || [])];
          newGroupValue[userIndex] = updatedUser;

          // return a new Group instance to preserve the Group type
          return new Group({
            allow_group_location_tracking: group.allow_group_location_tracking,
            group_id: group.group_id,
            group_name: group.group_name,
            group_value: newGroupValue,
            connect_user_auth_ids: group.connect_user_auth_ids,
            ops_user_auth_ids: group.ops_user_auth_ids,
            respond_user_auth_ids: group.respond_user_auth_ids,
          });
        }

        return group;
      }),
    );
  };
  /**
   * Updates the location tracking setting for a specific group
   * @param groupName - The name of the group to update
   * @param allowLocationTracking - The new location tracking setting
   */
  const updateGroupLocationSetting = (
    groupName: string,
    allowLocationTracking: boolean,
  ) => {
    setGroups((prevGroups) =>
      (prevGroups || []).map((group) => {
        if (!group || group.group_name !== groupName) {
          return group;
        }

        // Return a new Group instance with updated setting
        return new Group({
          allow_group_location_tracking: allowLocationTracking,
          group_id: group.group_id,
          group_name: group.group_name,
          group_value: group.group_value,
          connect_user_auth_ids: group.connect_user_auth_ids,
          ops_user_auth_ids: group.ops_user_auth_ids,
          respond_user_auth_ids: group.respond_user_auth_ids,
        });
      }),
    );
  };

  return (
    <OrganizationContext.Provider
      value={{
        groups,
        groupsStatus,
        groupsError,
        getUsersGroup,
        organization,
        updateOrganization,
        setUser,
        joinRequests,
        joinRequestsStatus,
        joinRequestsError,
        setJoinRequests,
        updateGroupLocationSetting,
        pvdConfigurations,
        emergencyConfigurations,
        loadPvdConfigs,
        loadEmergencyConfigs,
        loadAllConfigs,
        loadGroups,
        pvdConfigurationError,
        emergencyConfigurationError,
        // Legacy compatibility — removed in Task 30. Consumers in the
        // dashboard files migrate to the paired slots in Phase H.
        configurations: pvdConfigurations as CompassUnifiedConfigurationOnAzure[] | undefined,
        loadConfigs: loadAllConfigs,
        configurationError: pvdConfigurationError || emergencyConfigurationError,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationUpdaterProvider = (): OrganizationContextType => {
  return useContext(OrganizationContext);
};
